import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import io from 'socket.io-client';
import {
  FaCalendarAlt, FaComments,
  FaMoneyBillWave, FaCrown,
  FaVoteYea, FaUsers, FaPalette, FaHandHoldingUsd
} from 'react-icons/fa';
import { Shield, TreePine } from 'lucide-react';
import { toast } from 'sonner';

// Import extracted components
import Header from '../components/Header';
import { EventsTab, RolesTab, MembersTab, ChatTab, DuesTab, ContributionsTab, GovernanceTab, VaultTab, SettingsTab, FamilyTreeTab } from '../components/family';
import { usePermissions } from '../hooks/usePermissions';
import { GatedTab, FEATURES } from '../components/FeatureGate';
import { HelpTooltip } from '../components/HelpTooltip';
import { useFamilyTheme } from '../context/FamilyThemeContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function FamilyDetails() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { setActiveFamilyId, updateColors } = useFamilyTheme();
  
  // Permissions hook
  const { 
    permissions, 
    roles: userRoles, 
    isOwner, 
    hasPermission, 
    hasAnyPermission,
    loading: permissionsLoading,
    refetch: refetchPermissions 
  } = usePermissions(familyId);
  
  // State
  const [family, setFamily] = useState(null);
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [proxyMembers, setProxyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check for tab query parameter (e.g., ?tab=settings)
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'events');
  
  // Update tab when URL changes
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);
  
  // Socket.IO
  const [socket, setSocket] = useState(null);
  
  // Governance state
  const [votings, setVotings] = useState([]);
  const [showVotingForm, setShowVotingForm] = useState(false);
  
  // Forms
  const [showEventForm, setShowEventForm] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [messageInput, setMessageInput] = useState('');

  useEffect(() => {
    fetchFamilyData();
    
    // Initialize Socket.IO
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling']
    });
    
    newSocket.on('connect', () => {
      
      newSocket.emit('join_family', { family_id: familyId });
    });
    
    newSocket.on('new_message', (message) => {
      // Prevent duplicate messages (already added via API response)
      setMessages(prev => {
        const exists = prev.some(m => m.message_id === message.message_id);
        if (exists) return prev;
        return [...prev, message];
      });
    });
    
    setSocket(newSocket);
    
    return () => {
      if (newSocket) {
        newSocket.emit('leave_family', { family_id: familyId });
        newSocket.disconnect();
      }
    };
  }, [familyId]);

  const fetchFamilyData = async () => {
    try {
      const [familyRes, eventsRes, rolesRes, messagesRes, proxyRes, votingsRes] = await Promise.all([
        axios.get(`${API}/families/${familyId}`, { withCredentials: true }),
        axios.get(`${API}/families/${familyId}/events`, { withCredentials: true }),
        axios.get(`${API}/families/${familyId}/roles`, { withCredentials: true }),
        axios.get(`${API}/families/${familyId}/messages`, { withCredentials: true }),
        axios.get(`${API}/families/${familyId}/proxy-members`, { withCredentials: true }),
        axios.get(`${API}/families/${familyId}/votings`, { withCredentials: true })
      ]);
      
      const familyData = familyRes.data;
      setFamily(familyData);
      setEvents(eventsRes.data);
      setRoles(rolesRes.data);
      setMessages(messagesRes.data);
      setProxyMembers(proxyRes.data);
      setVotings(votingsRes.data);
      
      // Set active family for global theming
      setActiveFamilyId(familyId);
      if (familyData.ancestral_colors) {
        updateColors(familyData.ancestral_colors);
      }
    } catch (error) {
      console.error('Error fetching family data:', error);
      if ([403, 404].includes(error.response?.status)) {
        toast.error("You don't have access to this family", { id: 'family-no-access' });
        navigate('/dashboard');
        return;
      }
      alert('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e, priority = 'normal', mentions = []) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    const messageContent = messageInput;
    setMessageInput(''); // Clear input immediately for better UX

    try {
      const response = await axios.post(
        `${API}/messages`,
        {
          family_id: familyId,
          content: messageContent,
          priority: priority,
          mentions: mentions
        },
        { withCredentials: true }
      );
      
      // Add message from API response (Socket.IO will be filtered to prevent duplicates)
      const newMessage = response.data;
      setMessages(prev => {
        // Check if message already exists to prevent duplicates
        const exists = prev.some(m => m.message_id === newMessage.message_id);
        if (exists) return prev;
        return [...prev, newMessage];
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageInput(messageContent); // Restore input on error
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading family details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="family-details">
      {/* Header */}
      <Header variant="family" family={family} familyId={familyId} />

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <TabButton
              icon={<FaCalendarAlt />}
              label="Events"
              active={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
              dataTestId="tab-events"
              color="blue"
              helpFeature="events"
              badge={
                (isOwner || (hasPermission && hasPermission('manage_events')))
                  ? events.filter((e) => e.status === 'pending_approval').length
                  : 0
              }
            />
            <GatedTab
              feature={FEATURES.ASSIGN_ROLES}
              icon={<FaCrown />}
              label="Roles"
              active={activeTab === 'roles'}
              onClick={() => setActiveTab('roles')}
              dataTestId="tab-roles"
              color="amber"
            />
            <TabButton
              icon={<FaUsers />}
              label="Members"
              active={activeTab === 'members'}
              onClick={() => setActiveTab('members')}
              dataTestId="tab-members"
              color="green"
              helpFeature="members"
            />
            <TabButton
              icon={<TreePine className="w-4 h-4" />}
              label="Family Tree"
              active={activeTab === 'tree'}
              onClick={() => setActiveTab('tree')}
              dataTestId="tab-tree"
              color="emerald"
            />
            <TabButton
              icon={<FaComments />}
              label="Chat"
              active={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
              dataTestId="tab-chat"
              color="purple"
              helpFeature="chat"
            />
            <GatedTab
              feature={FEATURES.DUES_MANAGEMENT}
              icon={<FaMoneyBillWave />}
              label="Dues"
              active={activeTab === 'dues'}
              onClick={() => setActiveTab('dues')}
              dataTestId="tab-dues"
              color="emerald"
            />
            <TabButton
              icon={<FaHandHoldingUsd />}
              label="Contributions"
              active={activeTab === 'contributions'}
              onClick={() => setActiveTab('contributions')}
              dataTestId="tab-contributions"
              color="green"
              helpFeature="contributions"
            />
            <GatedTab
              feature={FEATURES.VOTING_GOVERNANCE}
              icon={<FaVoteYea />}
              label="Governance"
              active={activeTab === 'governance'}
              onClick={() => setActiveTab('governance')}
              dataTestId="tab-governance"
              color="rose"
            />
            <GatedTab
              feature={FEATURES.LEGAL_VAULT}
              icon={<Shield className="w-4 h-4" />}
              label="Vault"
              active={activeTab === 'vault'}
              onClick={() => setActiveTab('vault')}
              dataTestId="tab-vault"
              color="cyan"
            />
            {isOwner && (
              <TabButton
                icon={<FaPalette />}
                label="Family Colors"
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
                dataTestId="tab-settings"
                color="purple"
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {activeTab === 'events' && (
          <EventsTab
            events={events}
            setEvents={setEvents}
            familyId={familyId}
            showForm={showEventForm}
            setShowForm={setShowEventForm}
            onEventCreated={(newEvent) => setEvents([...events, newEvent])}
            hasPermission={hasPermission}
            isOwner={isOwner}
            currentUser={user}
            ancestralColors={family?.ancestral_colors}
          />
        )}

        {activeTab === 'roles' && (
          <RolesTab
            roles={roles}
            familyMembers={family?.members || []}
            familyId={familyId}
            showForm={showRoleForm}
            setShowForm={setShowRoleForm}
            onRoleCreated={(newRole) => {
              setRoles([...roles, newRole]);
              refetchPermissions(); // Refresh permissions when roles change
            }}
            hasPermission={hasPermission}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'members' && (
          <MembersTab
            members={family?.members || []}
            proxyMembers={proxyMembers}
            familyId={familyId}
            family={family}
            currentUser={user}
            showForm={showMemberForm}
            setShowForm={setShowMemberForm}
            onMemberAdded={fetchFamilyData}
            hasPermission={hasPermission}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'chat' && (
          <ChatTab
            messages={messages}
            messageInput={messageInput}
            setMessageInput={setMessageInput}
            onSendMessage={handleSendMessage}
            currentUser={user}
            familyMembers={family?.members || []}
            familyId={familyId}
            hasPermission={hasPermission}
          />
        )}

        {activeTab === 'dues' && (
          <DuesTab
            familyId={familyId}
            family={family}
            members={family?.members || []}
            proxyMembers={proxyMembers}
            currentUser={user}
            hasPermission={hasPermission}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'contributions' && (
          <ContributionsTab
            familyId={familyId}
            family={family}
            members={family?.members || []}
            proxyMembers={proxyMembers}
            currentUser={user}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'tree' && (
          <FamilyTreeTab
            familyId={familyId}
            family={family}
            currentUser={user}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'governance' && (
          <GovernanceTab
            familyId={familyId}
            votings={votings}
            setVotings={setVotings}
            roles={roles}
            familyMembers={family?.members || []}
            currentUser={user}
            showForm={showVotingForm}
            setShowForm={setShowVotingForm}
            hasPermission={hasPermission}
            isOwner={isOwner}
          />
        )}

        {activeTab === 'vault' && (
          <VaultTab
            familyId={familyId}
            currentUser={user}
            isOwner={isOwner}
            familyMembers={family?.members || []}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            familyId={familyId}
            family={family}
            isOwner={isOwner}
            onFamilyUpdate={fetchFamilyData}
            onClose={() => {
              setActiveTab('events');
              setSearchParams({}); // Clear URL params
            }}
          />
        )}
      </main>
    </div>
  );
}

// Tab Button Component with Help Tooltip
function TabButton({ icon, label, active, onClick, dataTestId, color = 'blue', helpFeature, badge = 0 }) {
  const colorClasses = {
    blue: 'text-blue-600',
    amber: 'text-amber-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    cyan: 'text-cyan-600',
    gray: 'text-gray-600',
  };
  
  const borderClasses = {
    blue: 'border-blue-600',
    amber: 'border-amber-600',
    green: 'border-green-600',
    purple: 'border-purple-600',
    emerald: 'border-emerald-600',
    rose: 'border-rose-600',
    cyan: 'border-cyan-600',
    gray: 'border-gray-600',
  };

  return (
    <div className="flex items-center">
      <button
        onClick={onClick}
        className={`group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-3 sm:py-4 border-b-2 font-medium transition text-xs sm:text-sm ${colorClasses[color]} ${
          active
            ? borderClasses[color]
            : 'border-transparent hover:opacity-80'
        }`}
        data-testid={dataTestId}
        title={label}
      >
        {icon}
        <span>{label}</span>
        {badge > 0 && (
          <span
            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-bold text-white bg-red-600 rounded-full leading-none"
            data-testid={`${dataTestId}-badge`}
            aria-label={`${badge} pending`}
          >
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
      {helpFeature && (
        <div className="ml-1 hidden sm:block">
          <HelpTooltip feature={helpFeature} size="sm" position="bottom" />
        </div>
      )}
    </div>
  );
}

export default FamilyDetails;
